/*
 * TEMPORARY — demo only.
 *
 * The ticlick-pricing reference plugin's config/config.js, ported verbatim:
 * same plans, same prices, same allowances, same add-ons, same copy. All money
 * is integer cents, exactly as the source holds it.
 *
 * This is the reference's own Cultural Exchange & J-1 pricing, NOT the CRM's.
 * It exists so the demo page matches preview.html figure for figure, and is
 * intended to be reverted afterwards.
 */

import type { DemoConfig } from './types';

export const demoConfig: DemoConfig = {
  "version": "1.0.0",
  "currency": "USD",
  "currency_symbol": "$",
  "default_segment": "cultural_exchange",
  "gtm": {
    "container_id": "GTM-5KP8C5N2",
    "events": {
      "plan_selected": "plan_selected",
      "addon_toggled": "addon_toggled",
      "begin_checkout": "begin_checkout"
    }
  },
  "segments": {
    "cultural_exchange": {
      "key": "cultural_exchange",
      "label": "Cultural Exchange & J-1",
      "default_plan": "advanced",
      "default_billing": "annual",
      "plan_order": [
        "basic",
        "essential",
        "advanced",
        "pro"
      ],
      "billing": {
        "annual": {
          "key": "annual",
          "label": "Annual",
          "note": "Save 20%"
        },
        "monthly": {
          "key": "monthly",
          "label": "Monthly",
          "note": ""
        }
      },
      "plans": {
        "basic": {
          "key": "basic",
          "name": "Basic",
          "description": "Solo coordinators and new agencies placing participants directly",
          "custom": false,
          "badge": "",
          "compare_to": "",
          "price": {
            "annual_per_month_cents": 22900,
            "annual_total_cents": 274800,
            "monthly_per_month_cents": 27500
          },
          "headline": {
            "value": 300,
            "label": "Up to 300 participants a year"
          },
          "bullets": [
            "5 users included",
            "Unlimited placements",
            "50 programs, 5K leads a year",
            "Email support in 3-5 business days"
          ],
          "seats": {
            "included": 5,
            "extra_monthly_cents": 3500,
            "extra_annual_cents": 42000,
            "hard_stop_total": 10
          },
          "participants_included": 300,
          "partners_included": 0
        },
        "essential": {
          "key": "essential",
          "name": "Essential",
          "description": "Growing agencies running multiple programs without a partner network",
          "custom": false,
          "badge": "",
          "compare_to": "",
          "price": {
            "annual_per_month_cents": 39900,
            "annual_total_cents": 478800,
            "monthly_per_month_cents": 47900
          },
          "headline": {
            "value": 500,
            "label": "Up to 500 participants a year"
          },
          "bullets": [
            "10 users included",
            "Multi-organisation support",
            "100 programs, 20K leads a year",
            "Email support within 72 hours"
          ],
          "seats": {
            "included": 10,
            "extra_monthly_cents": 3000,
            "extra_annual_cents": 36000,
            "hard_stop_total": 20
          },
          "participants_included": 500,
          "partners_included": 0
        },
        "advanced": {
          "key": "advanced",
          "name": "Advanced",
          "description": "Established agencies and sponsors working through host organisations and partners",
          "custom": false,
          "badge": "Most Popular",
          "compare_to": "essential",
          "price": {
            "annual_per_month_cents": 49900,
            "annual_total_cents": 598800,
            "monthly_per_month_cents": 59900
          },
          "headline": {
            "value": 1000,
            "label": "Up to 1,000 participants a year"
          },
          "bullets": [
            "15 users included",
            "Unlimited appointments",
            "SMS and unlimited email",
            "Email support within 24 hours"
          ],
          "seats": {
            "included": 15,
            "extra_monthly_cents": 2500,
            "extra_annual_cents": 30000,
            "hard_stop_total": 30
          },
          "participants_included": 1000,
          "partners_included": 5
        },
        "pro": {
          "key": "pro",
          "name": "Pro",
          "description": "High-volume sponsors and multi-branch exchange organisations",
          "custom": false,
          "badge": "",
          "compare_to": "",
          "price": {
            "annual_per_month_cents": 99900,
            "annual_total_cents": 1198800,
            "monthly_per_month_cents": 119900
          },
          "headline": {
            "value": 4000,
            "label": "Up to 4,000 participants a year"
          },
          "bullets": [
            "20 users included",
            "Unlimited leads and appointments",
            "500 programs, 5 TB storage",
            "Dedicated support"
          ],
          "seats": {
            "included": 20,
            "extra_monthly_cents": 2000,
            "extra_annual_cents": 24000,
            "hard_stop_total": 40
          },
          "participants_included": 4000,
          "partners_included": 10
        }
      },
      "addons": {
        "tia": {
          "key": "tia",
          "name": "Tia",
          "subtitle": "Ticlick Intelligence Assistant",
          "type": "toggle",
          "description": "Your AI assistant for the back office: qualifies and lists leads on its own, tracks how your team is performing and builds your reports.",
          "note": "",
          "price_from": true,
          "explainer_label": "What does Tia actually do?",
          "explainer": "It works across the data you already hold. It qualifies incoming leads and lists them for you automatically, tracks how each coordinator is performing, and generates the reports you would otherwise build by hand - among other jobs your team currently does manually. Think of it as working on your records and your team, rather than on your conversations. Pricing starts at this rate and depends on how much you use it.",
          "monthly_cents": 8900,
          "annual_cents": 106800,
          "annual_only": false,
          "available_on": [
            "essential",
            "advanced",
            "pro"
          ],
          "unavailable_note": "Available from Essential - upgrade to add",
          "enabled": true,
          "order": 10
        },
        "mobile_app": {
          "key": "mobile_app",
          "name": "Student Mobile App",
          "type": "toggle",
          "description": "Your own app on the App Store and Google Play, under your logo and your name.",
          "explainer_label": "What your students get from this",
          "explainer": "Your app sits on their home screen instead of a browser tab they have to find again. Push notifications reach them about a document deadline or an interview time, rather than an email they never open. They can check their placement, upload documents and message their coordinator from their phone, wherever they are. And it carries your name and your logo on the App Store and Google Play, so your agency looks like the established operation it is. The web student portal stays included on every plan for anyone who prefers it.",
          "monthly_cents": 8900,
          "annual_cents": 106800,
          "annual_only": false,
          "available_on": [
            "essential",
            "advanced",
            "pro"
          ],
          "unavailable_note": "Available from Essential - upgrade to add",
          "enabled": true,
          "order": 20
        },
        "omnichannel": {
          "key": "omnichannel",
          "name": "Unified Inbox with 24/7 AI Replies",
          "type": "toggle",
          "description": "WhatsApp, Instagram and Facebook in one inbox. The AI answers day and night, and qualifies every lead and participant as they arrive.",
          "price_from": true,
          "price_note": "for 1,000 contacts and its own messaging allowance",
          "explainer_label": "What your students get from this",
          "explainer": "They message you wherever they already are - WhatsApp, Instagram or Facebook - instead of working out which channel you actually read. They get an answer straight away, at any hour and in any time zone, rather than waiting for your office to open. And because every conversation lands in one place, whoever picks it up can already see what was said before, so nobody has to tell their story twice. The messaging allowance is its own, so none of this eats into your plan's WhatsApp limit.",
          "monthly_cents": 5900,
          "annual_cents": 70800,
          "annual_only": false,
          "available_on": [
            "basic",
            "essential",
            "advanced",
            "pro"
          ],
          "unavailable_note": "",
          "enabled": true,
          "order": 30
        },
        "partners": {
          "key": "partners",
          "name": "Partners Management",
          "type": "quantity",
          "pricing_model": "partner_ladder",
          "description": "Manage host organisations, agents and partner placements.",
          "note": "Billed annually. Advanced includes 5 partners, Pro includes 10, for the same base price.",
          "monthly_cents": 0,
          "annual_cents": 100000,
          "annual_only": true,
          "available_on": [
            "advanced",
            "pro"
          ],
          "unavailable_note": "Available from Advanced - upgrade to add",
          "unit_label": "partners",
          "min": 0,
          "max": 60,
          "step": 1,
          "enabled": false,
          "order": 40
        },
        "extra_org": {
          "key": "extra_org",
          "name": "Additional organisation",
          "type": "quantity",
          "pricing_model": "linear",
          "description": "Run a second or third legal entity in the same account, with separate records.",
          "note": "Billed annually.",
          "monthly_cents": 0,
          "annual_cents": 200000,
          "annual_only": true,
          "available_on": [
            "essential",
            "advanced",
            "pro"
          ],
          "unavailable_note": "Available from Essential - upgrade to add",
          "unit_label": "organisations",
          "min": 0,
          "max": 20,
          "step": 1,
          "enabled": false,
          "order": 50
        }
      },
      "partner_ladder": {
        "base_annual_cents": 100000,
        "bands": [
          {
            "from": 6,
            "to": 20,
            "per_partner_annual_cents": 18000
          },
          {
            "from": 21,
            "to": 40,
            "per_partner_annual_cents": 15000
          }
        ],
        "flex_above": 40,
        "flex_reason": "More than 40 partners is priced individually - let's size it with you."
      },
      "participants": {
        "unit_cents": 500,
        "block_size": 50,
        "max_blocks": 100,
        "annual_only": true,
        "unit_label": "participants",
        "overage_policy": "If you exceed your participant allowance, additional participants are billed at $5 each. You can upgrade to a higher plan at any time, and we'll credit the unused portion of your current plan against the new one. Your renewal date does not change."
      },
      "seats": {
        "unit_label": "users",
        "flex_overshoot": 5,
        "flex_reason": "More users than this plan supports - larger teams are quoted directly."
      },
      "comparison": [
        {
          "group": "Capacity",
          "rows": [
            {
              "label": "Users included",
              "values": {
                "basic": "5",
                "essential": "10",
                "advanced": "15",
                "pro": "20"
              }
            },
            {
              "label": "Participants",
              "highlight": true,
              "values": {
                "basic": "300",
                "essential": "500",
                "advanced": "1,000",
                "pro": "4,000"
              }
            },
            {
              "label": "Placements (job offers / internships)",
              "values": {
                "basic": "Unlimited",
                "essential": "Unlimited",
                "advanced": "Unlimited",
                "pro": "Unlimited"
              }
            },
            {
              "label": "Programs",
              "values": {
                "basic": "50",
                "essential": "100",
                "advanced": "200",
                "pro": "500"
              }
            },
            {
              "label": "Leads",
              "values": {
                "basic": "5K/yr",
                "essential": "20K/yr",
                "advanced": "50K/yr",
                "pro": "Unlimited"
              }
            },
            {
              "label": "Storage",
              "values": {
                "basic": "500 GB",
                "essential": "1 TB",
                "advanced": "3 TB",
                "pro": "5 TB"
              }
            },
            {
              "label": "Active appointments",
              "values": {
                "basic": "500",
                "essential": "1,000",
                "advanced": "Unlimited",
                "pro": "Unlimited"
              }
            },
            {
              "label": "Activity logs",
              "values": {
                "basic": "90 days",
                "essential": "180 days",
                "advanced": "365 days",
                "pro": "Unlimited"
              }
            },
            {
              "label": "Multi-organisation",
              "values": {
                "basic": "No",
                "essential": "Yes",
                "advanced": "Yes",
                "pro": "Yes"
              }
            }
          ]
        },
        {
          "group": "Scale & limits",
          "rows": [
            {
              "label": "Maximum users",
              "values": {
                "basic": "10",
                "essential": "20",
                "advanced": "30",
                "pro": "40"
              }
            },
            {
              "label": "Extra user",
              "values": {
                "basic": "$35/mo",
                "essential": "$30/mo",
                "advanced": "$25/mo",
                "pro": "$20/mo"
              }
            },
            {
              "label": "Partners included",
              "values": {
                "basic": "None",
                "essential": "None",
                "advanced": "5",
                "pro": "10"
              }
            },
            {
              "label": "Extra participants",
              "values": {
                "basic": "$5 each",
                "essential": "$5 each",
                "advanced": "$5 each",
                "pro": "$5 each"
              }
            }
          ]
        },
        {
          "group": "Communication",
          "rows": [
            {
              "label": "WhatsApp",
              "values": {
                "basic": "Limited",
                "essential": "Yes",
                "advanced": "Yes",
                "pro": "Yes"
              }
            },
            {
              "label": "SMS",
              "values": {
                "basic": "No",
                "essential": "No",
                "advanced": "Yes",
                "pro": "Yes"
              }
            },
            {
              "label": "Email",
              "values": {
                "basic": "50K/yr",
                "essential": "120K/yr",
                "advanced": "Unlimited",
                "pro": "Unlimited"
              }
            },
            {
              "label": "Broadcast messages",
              "values": {
                "basic": "No",
                "essential": "Max 100/message",
                "advanced": "Max 500/message",
                "pro": "Max 2,000/message"
              }
            }
          ]
        },
        {
          "group": "Modules",
          "footnote": "Core modules = participants, programs, placements, leads, reminders, student portal.",
          "rows": [
            {
              "label": "Core modules",
              "footnote_ref": true,
              "values": {
                "basic": "Limited",
                "essential": "Basic",
                "advanced": "Advanced",
                "pro": "Advanced"
              }
            },
            {
              "label": "Partners Management",
              "values": {
                "basic": "Not available",
                "essential": "Not available",
                "advanced": "Add-on",
                "pro": "Add-on"
              }
            },
            {
              "label": "Accounting & invoices",
              "values": {
                "basic": "Yes",
                "essential": "Yes",
                "advanced": "Yes",
                "pro": "Yes"
              }
            },
            {
              "label": "Tasks",
              "values": {
                "basic": "Yes",
                "essential": "Yes",
                "advanced": "Yes",
                "pro": "Yes"
              }
            },
            {
              "label": "Online payment",
              "values": {
                "basic": "Yes",
                "essential": "Yes",
                "advanced": "Yes",
                "pro": "Yes"
              }
            }
          ]
        },
        {
          "group": "Add-ons",
          "footnote": "Add-ons are optional and priced separately. Configure them above.",
          "rows": [
            {
              "label": "Tia (Ticlick Intelligence Assistant)",
              "values": {
                "basic": "Not available",
                "essential": "Add-on",
                "advanced": "Add-on",
                "pro": "Add-on"
              }
            },
            {
              "label": "Student Mobile App",
              "values": {
                "basic": "Not available",
                "essential": "Add-on",
                "advanced": "Add-on",
                "pro": "Add-on"
              }
            },
            {
              "label": "Unified Inbox with 24/7 AI Replies",
              "values": {
                "basic": "Add-on",
                "essential": "Add-on",
                "advanced": "Add-on",
                "pro": "Add-on"
              }
            }
          ]
        },
        {
          "group": "Platform & support",
          "rows": [
            {
              "label": "Integrations",
              "values": {
                "basic": "No",
                "essential": "Basic",
                "advanced": "Advanced",
                "pro": "Advanced"
              }
            },
            {
              "label": "Support",
              "values": {
                "basic": "Email, 3-5 business days",
                "essential": "Email within 72 hrs",
                "advanced": "Email within 24 hrs",
                "pro": "Dedicated support"
              }
            }
          ]
        }
      ],
      "module_levels": {
        "Limited": "View and manage records with standard fields",
        "Basic": "Adds custom fields, bulk actions and saved views",
        "Advanced": "Adds automation rules, custom workflows and granular permissions"
      },
      "copy": {
        "eyebrow": "",
        "heading": "Pricing built around the participants you place",
        "subheading": "Every plan starts with a 14-day free trial. Pick a plan, add what you need, and see your total before you talk to anyone.",
        "billing_toggle_label": "Billing period",
        "configure_heading": "Configure your plan",
        "configure_subheading": "Everything below is optional. Your total updates as you go.",
        "summary_heading": "Your plan",
        "compare_toggle_open": "Compare all plans",
        "compare_toggle_close": "Hide comparison",
        "compare_heading": "Full plan comparison",
        "checkout_cta": "Continue to checkout",
        "trial_cta": "Start your 14-day free trial",
        "trial_note": "No credit card needed",
        "sales_cta": "Talk to sales",
        "custom_plan_cta": "Talk to sales",
        "per_month": "/mo",
        "per_year": "/yr",
        "billed_annually_note": "billed annually",
        "billed_monthly_note": "billed monthly",
        "annual_extras_heading": "Billed once annually",
        "annual_extras_note": "These items top up your annual allowance and are billed once a year, alongside your first payment.",
        "seats_label": "Additional users",
        "seats_help": "Extra seats beyond the users included in your plan.",
        "participants_label": "Additional participants",
        "participants_help": "Added to your annual participant allowance, in blocks of 50.",
        "removed_notice_title": "We updated your configuration",
        "withdrawn_addon_note": "This is no longer available to buy online - talk to sales if you still need it.",
        "unknown_plan_note": "The plan in your link is no longer available.",
        "flex_notice_title": "This configuration needs a custom quote",
        "empty_extras": "No extras added yet."
      }
    }
  }
};
